import React from 'react';
import { Link } from 'react-router-dom';
import { ICompany } from 'types/company';
import useResource from 'util/resource/useResource';
import Resources from 'util/resource/Resources';
import { getOrUndefined } from 'util/resource';
import { getDateRange } from 'util/dateUtils';
import { companyUrls } from 'urls';
import StatsCard from 'ui/views/cards/StatsCard';
import Button from 'ui/elements/buttons/Button';
import { companiesAPIUrls } from 'apis/CompanyAPI/companies/companiesApi';
import { PostReach } from 'apis/CompanyAPI/insights/types';
import { NUMBER_OF_DAYS_IN_INSIGHTS_PERIOD } from './Manage';

function usePostReach(company: ICompany) {
  const [startDate, endDate] = getDateRange(new Date(), NUMBER_OF_DAYS_IN_INSIGHTS_PERIOD);
  return useResource<PostReach>(companiesAPIUrls.insights.postReach(company.id, startDate, endDate)).resource;
}

export default function PostReachCard({ company }: { company: ICompany }) {
  const postReach = usePostReach(company);

  return (
    <Resources resources={[postReach]}>
      {([{ reach, newAudience, postsInPeriod }]) => {
        const hasNotPosted = postsInPeriod === 0 && reach.value === 0;
        return (
          <StatsCard
            className="u-flex-grow"
            title="Post reach"
            value={reach.value}
            previousValue={reach.previousValue}
            tooltip={`People who saw your posts in the last ${NUMBER_OF_DAYS_IN_INSIGHTS_PERIOD} days: in their feed, on your profile, on company cards, or by opening them.`}
          >
            {hasNotPosted ? (
              <>
                <p className="text-small u-half-spacing-top">
                  Companies get more investor views the week after they post.
                </p>
                <Link to={companyUrls.overview(company.slug, 'updates', { isPostingUpdate: true })}>
                  <Button className="u-content-spacing-top" style={{ width: '100%' }} kind="primary">
                    Post update
                  </Button>
                </Link>
              </>
            ) : (
              <>
                {newAudience.value > 0 && (
                  <p className="text-small u-half-spacing-top">{newAudience.value} new to your company</p>
                )}
                <Link to={companyUrls.insights(company.slug)}>
                  <Button className="u-content-spacing-top" style={{ width: '100%' }} kind="secondary" color="grey">
                    See more
                  </Button>
                </Link>
              </>
            )}
          </StatsCard>
        );
      }}
    </Resources>
  );
}

/** Post reach once loaded, for rows that shouldn't show a spinner of their own */
export function usePostReachValue(company: ICompany) {
  return getOrUndefined(usePostReach(company));
}
